import { useEffect, useState } from 'react';
import { SettingsFrame } from '@/modules/settings/components/SettingsFrame';
import { clsx } from 'clsx';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Input } from '@/shared/ui/Input';
import { useToast } from '@/shared/ui/Toast';
import { useLocation } from '@/shared/hooks/useLocation';
import { errorMessage, useCanEditProgram } from '@/modules/reviews/sms/hooks';
import type { RegistrationPrefill } from '@/modules/reviews/api/reviewSmsApi';
import { textingApi, type TextingSetup } from '../api/textingApi';
import { reviewKeys } from '@/modules/reviews/queryKeys';
import { textingKeys } from '../queryKeys';
import styles from './TextingSettings.module.css';

/**
 * Store the new setup, and refetch the review program: a number going live or a registration
 * approved here can promote it to live, which Reviews → Get reviews must show at once.
 */
function saveTextingSetup(queryClient: QueryClient, locationId: string, next: TextingSetup) {
  queryClient.setQueryData(textingKeys.setup(locationId), next);
  void queryClient.invalidateQueries({ queryKey: reviewKeys.smsProgram(locationId) });
}

type Choice = 'new_local' | 'practice_hosted';

const OPTIONS: Array<{ value: Choice; title: string; hint: string; tag: string }> = [
  {
    value: 'new_local',
    title: 'A new local number',
    hint: 'A number in your area code that texts patients. Calls to it forward to your front desk. Ready in days.',
    tag: 'Recommended',
  },
  {
    value: 'practice_hosted',
    title: 'Your practice number (hosted SMS)',
    hint: 'We text-enable the landline or VoIP number patients already know. Calls stay with your phone company. Takes 1–2 weeks and a signed authorization.',
    tag: 'Patients know it',
  },
];

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
}

function StatusPill({ tone, children }: { tone: 'ok' | 'wait' | 'todo'; children: string }) {
  const className =
    tone === 'ok' ? styles.pillOk : tone === 'wait' ? styles.pillWait : styles.pillTodo;
  return <span className={className}>{children}</span>;
}

// ── Step 1: the number ───────────────────────────────────────────────────────

function NumberCard({
  locationId,
  setup,
  canEdit,
  defaultPhone,
}: {
  locationId: string;
  setup: TextingSetup;
  canEdit: boolean;
  defaultPhone: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const n = setup.number;
  const [choice, setChoice] = useState<Choice>('new_local');
  const [areaCode, setAreaCode] = useState('');
  const [searchedCode, setSearchedCode] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [forwardTo, setForwardTo] = useState(defaultPhone);
  const [practiceNumber, setPracticeNumber] = useState(defaultPhone);
  // A blank search uses the location phone's area code (backend searchLocalNumbers).
  const locationAreaCode = /^\D*1?\D*(\d{3})\D*\d{3}\D*\d{4}\D*$/.exec(defaultPhone)?.[1] ?? null;

  const numbers = useQuery({
    queryKey: textingKeys.numbers(locationId, searchedCode ?? ''),
    queryFn: () => textingApi.searchNumbers(locationId, searchedCode || undefined),
    enabled: searchedCode !== null,
    retry: false,
  });
  const done = (next: TextingSetup) => saveTextingSetup(queryClient, locationId, next);
  const request = useMutation({
    mutationFn: () =>
      textingApi.requestNumber(
        locationId,
        choice === 'new_local'
          ? { mode: 'new_local', e164: picked!, forwardTo: forwardTo || null }
          : { mode: 'practice_hosted', e164: practiceNumber },
      ),
    onSuccess: (next) => {
      done(next);
      toast.success('Requested. PracticeNova sets it up and we’ll show it here once it’s live.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const check = useMutation({
    mutationFn: () => textingApi.checkNow(locationId),
    onSuccess: (next) => {
      done(next);
      toast.info(
        next.number.provisioningState === 'active'
          ? 'Your number is live.'
          : 'Not ready yet. We’ll keep checking.',
      );
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  if (n.provisioningState === 'active') {
    return (
      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>1. Texting number</h2>
          <StatusPill tone="ok">Live</StatusPill>
        </header>
        <p className={styles.body}>
          Texts go out from <b>{n.masked}</b>
          {n.mode === 'practice_hosted' ? ' (your practice number, hosted for SMS)' : ''}.
          {n.setupByPracticeNova ? ' Set up by PracticeNova.' : ''}
          {n.requestedLast4
            ? ` The number you asked for (ending ${n.requestedLast4}) wasn’t available, so PracticeNova connected this one.`
            : ''}
        </p>
      </section>
    );
  }

  if (n.provisioningState !== 'none' && n.provisioningState !== 'failed') {
    return (
      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>1. Texting number</h2>
          <StatusPill tone="wait">Requested</StatusPill>
        </header>
        <p className={styles.body}>
          You asked for <b>{n.masked}</b>
          {n.mode === 'practice_hosted' ? ' to be hosted for SMS' : ''}
          {n.requestedAt ? ` on ${formatDate(n.requestedAt)}` : ''}. PracticeNova is setting it up.
          We check every {setup.checkEveryHours} hours and carrier registration opens here as soon
          as it’s live.
        </p>
        {canEdit && (
          <div className={styles.row}>
            <Button
              variant="secondary"
              size="sm"
              isLoading={check.isPending}
              onClick={() => check.mutate()}
            >
              Check now
            </Button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>1. Texting number</h2>
        <StatusPill tone="todo">Not set up</StatusPill>
      </header>
      <p className={styles.body}>
        Pick the number patients get texts from. PracticeNova sets it up for you; nothing is charged
        or changed until we do.
      </p>
      {n.provisioningState === 'failed' && n.provisioningError && (
        <Callout variant="error" title="The last request didn’t go through">
          {n.provisioningError}
        </Callout>
      )}
      <div className={styles.choice} role="radiogroup" aria-label="Texting number">
        {OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={choice === o.value}
            className={clsx(styles.option, choice === o.value && styles.optionActive)}
            onClick={() => setChoice(o.value)}
            disabled={!canEdit}
          >
            <span className={styles.optionTitle}>
              {o.title}
              <span className={styles.tag}>{o.tag}</span>
            </span>
            <span className={styles.hint}>{o.hint}</span>
          </button>
        ))}
      </div>

      {choice === 'new_local' ? (
        <>
          <div className={styles.row}>
            <Input
              label="Area code"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder={
                locationAreaCode
                  ? `Leave blank to use ${locationAreaCode}`
                  : 'Three digits, e.g. 925'
              }
            />
            <Button
              variant="secondary"
              size="sm"
              isLoading={numbers.isFetching}
              disabled={!canEdit || (areaCode.length > 0 && areaCode.length < 3)}
              onClick={() => {
                setPicked(null);
                if (searchedCode === areaCode) void numbers.refetch();
                else setSearchedCode(areaCode);
              }}
            >
              Find numbers
            </Button>
          </div>
          {numbers.isError && (
            <Callout variant="error" title="Number search didn’t work">
              {errorMessage(numbers.error)}
            </Callout>
          )}
          {numbers.data && (
            <div className={styles.numberList}>
              {numbers.data.length === 0 && (
                <span className={styles.hint}>
                  No numbers are available in that area code right now. Try a nearby area code.
                </span>
              )}
              {numbers.data.map((num) => (
                <button
                  key={num.e164}
                  type="button"
                  className={clsx(
                    styles.numberChip,
                    picked === num.e164 && styles.numberChipActive,
                  )}
                  onClick={() => setPicked(num.e164)}
                  aria-pressed={picked === num.e164}
                >
                  {num.e164}
                  {num.region ? ` · ${num.region}` : ''}
                </button>
              ))}
            </div>
          )}
          <div className={styles.row}>
            <Input
              label="Forward calls to"
              value={forwardTo}
              onChange={(e) => setForwardTo(e.target.value)}
              placeholder="(925) 555-0142"
            />
          </div>
          <div className={styles.actions}>
            <Button
              disabled={!canEdit || !picked}
              isLoading={request.isPending}
              onClick={() => request.mutate()}
            >
              Request this number
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.row}>
            <Input
              label="Practice number"
              value={practiceNumber}
              onChange={(e) => setPracticeNumber(e.target.value)}
              placeholder="(925) 555-0100"
            />
          </div>
          <p className={styles.hint}>
            PracticeNova emails you a letter of authorization to sign and asks for a recent phone
            bill. If the number already texts through another service, that has to be switched off
            first.
          </p>
          <div className={styles.actions}>
            <Button
              disabled={!canEdit || !practiceNumber.trim()}
              isLoading={request.isPending}
              onClick={() => request.mutate()}
            >
              Request hosting
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

// ── Step 2: carrier registration ─────────────────────────────────────────────

function RegistrationCard({
  locationId,
  setup,
  canEdit,
}: {
  locationId: string;
  setup: TextingSetup;
  canEdit: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const reg = setup.registration;
  const submitted = !!reg.submittedAt;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<RegistrationPrefill | null>(null);
  const showForm = setup.registrationOpen && canEdit && !reg.approved && (!submitted || editing);

  const prefill = useQuery({
    queryKey: textingKeys.registrationPrefill(locationId),
    queryFn: () => textingApi.registrationPrefill(locationId),
    enabled: showForm,
  });
  useEffect(() => {
    if (prefill.data && !form) setForm(prefill.data);
  }, [prefill.data, form]);

  const submit = useMutation({
    mutationFn: () => textingApi.submitRegistration(locationId, { ...form!, path: '10dlc' }),
    onSuccess: (next) => {
      saveTextingSetup(queryClient, locationId, next);
      setEditing(false);
      toast.success('Sent to PracticeNova. We file it with the carriers and track approval here.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const check = useMutation({
    mutationFn: () => textingApi.checkNow(locationId),
    onSuccess: (next) => saveTextingSetup(queryClient, locationId, next),
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const setBrand = (key: keyof RegistrationPrefill['brand'], value: string) =>
    setForm((f) => (f ? { ...f, brand: { ...f.brand, [key]: value } } : f));

  const pill = reg.approved ? (
    <StatusPill tone="ok">Approved</StatusPill>
  ) : submitted ? (
    <StatusPill tone="wait">In review</StatusPill>
  ) : (
    <StatusPill tone="todo">
      {setup.registrationOpen ? 'Not submitted' : 'Waiting on step 1'}
    </StatusPill>
  );

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>2. Carrier registration (10DLC)</h2>
        {pill}
      </header>
      <p className={styles.body}>
        US carriers block business texts from unregistered numbers. PracticeNova registers your
        practice’s brand and texting campaign with the carriers. Nothing texts patients, tests
        included, until both are approved.
      </p>

      {!setup.registrationOpen && (
        <Callout variant="info">Opens once your texting number is live.</Callout>
      )}

      {setup.registrationOpen && !showForm && submitted && (
        <>
          <div className={styles.statusList}>
            <div className={styles.statusRow}>
              <span>Brand</span>
              <StatusPill tone={reg.brandState === 'approved' ? 'ok' : 'wait'}>
                {reg.brandState.replace('_', ' ')}
              </StatusPill>
            </div>
            <div className={styles.statusRow}>
              <span>Campaign</span>
              <StatusPill tone={reg.campaignState === 'approved' ? 'ok' : 'wait'}>
                {reg.campaignState.replace('_', ' ')}
              </StatusPill>
            </div>
          </div>
          <p className={styles.hint}>
            Submitted {formatDate(reg.submittedAt)}. Approval usually takes a few days and can take
            up to 2 weeks. We check every {setup.checkEveryHours} hours
            {reg.lastCheckedAt ? `; last checked ${formatDate(reg.lastCheckedAt)}` : ''}.
          </p>
          {reg.rejectionReason && (
            <Callout variant="error" title="The carriers sent it back">
              {reg.rejectionReason}
            </Callout>
          )}
          {canEdit && !reg.approved && (
            <div className={styles.row}>
              <Button
                variant="secondary"
                size="sm"
                isLoading={check.isPending}
                onClick={() => check.mutate()}
              >
                Check now
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                Edit and resend
              </Button>
            </div>
          )}
        </>
      )}

      {showForm &&
        (form ? (
          <>
            <p className={styles.hint}>We filled in what we know. Check it and add your EIN.</p>
            <div className={styles.row}>
              <Input
                label="Legal business name"
                value={form.brand.companyName}
                onChange={(e) => setBrand('companyName', e.target.value)}
              />
              <Input
                label="Name patients know"
                value={form.brand.displayName}
                onChange={(e) => setBrand('displayName', e.target.value)}
              />
              <Input
                label="EIN"
                value={form.brand.ein}
                onChange={(e) => setBrand('ein', e.target.value)}
                placeholder="12-3456789"
              />
            </div>
            <div className={styles.row}>
              <Input
                label="Street"
                value={form.brand.street}
                onChange={(e) => setBrand('street', e.target.value)}
              />
              <Input
                label="City"
                value={form.brand.city}
                onChange={(e) => setBrand('city', e.target.value)}
              />
              <Input
                label="State"
                value={form.brand.state}
                onChange={(e) => setBrand('state', e.target.value.toUpperCase().slice(0, 2))}
              />
              <Input
                label="ZIP"
                value={form.brand.postalCode}
                onChange={(e) => setBrand('postalCode', e.target.value)}
              />
            </div>
            <div className={styles.row}>
              <Input
                label="Website"
                value={form.brand.website}
                onChange={(e) => setBrand('website', e.target.value)}
              />
              <Input
                label="Contact email"
                value={form.brand.email}
                onChange={(e) => setBrand('email', e.target.value)}
              />
              <Input
                label="Business phone"
                value={form.brand.phone}
                onChange={(e) => setBrand('phone', e.target.value)}
              />
            </div>
            <div className={styles.statusList}>
              <div className={styles.statusRow}>
                <span>What the texts are for</span>
                <span className={styles.hint}>Customer care: asking about a visit afterwards</span>
              </div>
              <div className={styles.statusRow}>
                <span>How patients agree to texts</span>
                <span className={styles.hint}>{form.campaign.messageFlow}</span>
              </div>
              <div className={styles.statusRow}>
                <span>Sample messages</span>
                <span className={styles.hint}>{form.campaign.sampleMessages.length} included</span>
              </div>
            </div>
            <div className={styles.actions}>
              {submitted && (
                <Button variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              )}
              <Button
                disabled={!form.brand.ein.trim() || !form.brand.companyName.trim()}
                isLoading={submit.isPending}
                onClick={() => submit.mutate()}
              >
                Send to PracticeNova
              </Button>
            </div>
          </>
        ) : (
          <p className={styles.hint}>Loading what we know about the practice…</p>
        ))}
    </section>
  );
}

/**
 * Settings → Communication → Texting: the number a location texts from and the carrier registration. Every
 * texting feature (review requests today) needs both; PracticeNova sets them up by hand.
 */
export function TextingSettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const canEdit = useCanEditProgram();
  const setup = useQuery({
    queryKey: textingKeys.setup(locationId),
    queryFn: () => textingApi.get(locationId),
    enabled: !!locationId,
  });

  return (
    <SettingsFrame
      embedded={embedded}
      id="settings-texting"
      pageClassName={styles.page}
      title="Texting"
      subtitle={`The number ${currentLocation?.name ?? 'this location'} texts patients from, and its carrier registration`}
    >
      {setup.isLoading && <p className={styles.hint}>Loading…</p>}
      {setup.isError && (
        <Callout variant="error" title="Couldn’t load texting setup">
          {errorMessage(setup.error)}
        </Callout>
      )}
      {setup.data && (
        <>
          {/* Keyed by location: a switch must not carry one location's form into another's. */}
          <NumberCard
            key={`number-${locationId}`}
            locationId={locationId}
            setup={setup.data}
            canEdit={canEdit}
            defaultPhone={currentLocation?.phone ?? ''}
          />
          <RegistrationCard
            key={`registration-${locationId}`}
            locationId={locationId}
            setup={setup.data}
            canEdit={canEdit}
          />
        </>
      )}
    </SettingsFrame>
  );
}
